import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Auction } from '@sorare/core/src/assets/icons/Auction';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelM } from '@sorare/core/src/atoms/typography';
import { VerticalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { MY_SORARE_AUCTIONS } from '@sorare/core/src/constants/__generated__/routes';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import {
  STORAGE,
  useLocalStorage,
} from '@sorare/core/src/hooks/useLocalStorage';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { thickMaterial } from '@sorare/core/src/style/utils';

import { FlexCard } from 'components/token/FlexCard';
import { useBestBidBelongsToUser } from 'hooks/auctions/useBestBidBelongsToUser';
import { useAuctionCurrentPrice } from 'lib/auctions';

import { AuctionTimeLeft } from '../AuctionTimeLeft';
import type { MyBidsBanner_auctionConnection } from './__generated__/index.graphql';

export const MyBidsBannerWrapper = styled(Horizontal)`
  ${thickMaterial}
  position: sticky;
  bottom: 0;
  height: 80px;
  overflow: auto;
  border-top: 1px solid var(--c-nd-100);
`;
const IconContainer = styled(Auction)`
  width: 40px;
  height: 40px;
  background-color: var(--c-nd-150-opaque);
  border-radius: 50%;
  padding: var(--unit);
`;
const LiveCount = styled(Horizontal).attrs({ gap: 1, center: true })`
  width: 40px;
  background-color: var(--c-nd-150-opaque);
  border-radius: 20px;
  margin-top: calc(var(--unit) * -2);
`;
const PartialDivider = styled(VerticalDivider)`
  background-color: var(--c-nd-100);
  height: 50%;
  margin: 0 var(--unit);
  @media ${tabletAndAbove} {
    margin: 0 var(--intermediate-unit);
  }
`;
const StartIcon = styled(Vertical)`
  padding: 0 var(--double-unit);
  border-right: 1px solid var(--c-nd-150);
  height: 100%;
  align-items: center;
  justify-content: center;
`;
const CardWrapper = styled.div`
  width: 35px;
`;
const Auctions = styled(Horizontal).attrs({ gap: 1 })`
  padding: var(--unit);
  @media ${tabletAndAbove} {
    padding: var(--intermediate-unit);
  }
  height: 100%;
`;
const MySorareLink = styled(Link)`
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;
const CloseButtonWrapper = styled(CloseButton)`
  position: absolute;
  right: 0;
  top: calc(-1 * var(--unit));
  transition: opacity 0.2s ease-in-out;
  @media ${tabletAndAbove} {
    opacity: 0;
  }
`;
const BidItemWrapper = styled.div`
  position: relative;
  &:hover {
    ${CloseButtonWrapper} {
      opacity: 1;
    }
  }
`;

const getMessage = (winning: boolean, myLastBid: boolean) => {
  if (winning) return glossary.auctionWinning;
  if (myLastBid) return glossary.auctionOutbid;
  return glossary.auctionIdle;
};

const AuctionBidItem = ({
  auction,
  last,
  ignoreBid,
}: {
  auction: MyBidsBanner_auctionConnection['nodes'][number];
  last: boolean;
  ignoreBid: () => void;
}) => {
  const { bestBid, myLastBid } = auction;
  const bestBidBelongsToUser = useBestBidBelongsToUser();
  const winning = bestBidBelongsToUser(bestBid);
  const currentPriceMonetary = useAuctionCurrentPrice(auction);

  const { main } = useAmountWithConversion({
    monetaryAmount: currentPriceMonetary,
  });

  return (
    <BidItemWrapper>
      <Horizontal>
        <CardWrapper>
          <FlexCard card={auction.anyCards[0]} width={40} withLink />
        </CardWrapper>
        <Vertical gap={0.5}>
          <LabelM color="var(--c-nd-700)">
            {auction.anyCards[0].anyPlayer.displayName}
          </LabelM>
          <Horizontal>
            <LabelM
              bold
              color={winning ? 'var(--c-onBase-success)' : 'var(--c-red-300)'}
            >
              <FormattedMessage {...getMessage(!!winning, !!myLastBid)} />
            </LabelM>
            <LabelM color="var(--c-nd-300)"> • </LabelM>
            <LabelM color="var(--c-nd-700)">{main}</LabelM>
          </Horizontal>
          <Horizontal>
            <FontAwesomeIcon icon={faClock} size="sm" color="var(--c-nd-400)" />
            <LabelM bold>
              <AuctionTimeLeft
                endDate={auction.endDate}
                forceInlineLayout
                withExplicitTime={false}
              />
            </LabelM>
          </Horizontal>
        </Vertical>
        <CloseButtonWrapper transparent size="compact" onClose={ignoreBid} />
      </Horizontal>
      {!last && <PartialDivider />}
    </BidItemWrapper>
  );
};

type Props = {
  auctions: Nullable<MyBidsBanner_auctionConnection>;
  loading: boolean;
  loadMore: (
    loadMore: boolean,
    options: { after: string | undefined | null }
  ) => void;
};

export const MyBidsBanner = ({ auctions, loading, loadMore }: Props) => {
  const { up: isTabletOrAbove } = useScreenSize('tablet');
  const [ignoredBids, setIgnoredBids] = useLocalStorage<string[]>(
    STORAGE.myBidsBannerIgnoredBids,
    []
  );

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        after: auctions?.pageInfo.endCursor,
      });
    },
    !!auctions?.pageInfo.hasNextPage,
    loading
  );
  const auctionsToShow = auctions?.nodes.filter(
    auction => !ignoredBids.includes(auction.id)
  );

  if (!auctionsToShow?.length) return null;

  return (
    <ConditionalWrapper
      wrap={!isTabletOrAbove}
      Wrapper={Portal}
      props={{
        id: 'above-bottom-bar-portal',
        style: {
          order: '99',
        },
      }}
    >
      <MyBidsBannerWrapper>
        <MySorareLink to={generatePath(MY_SORARE_AUCTIONS)}>
          <StartIcon>
            <IconContainer />
            <LiveCount>
              <LiveDot size="sm" />
              <BodyS bold>{auctions?.totalCount}</BodyS>
            </LiveCount>
          </StartIcon>
        </MySorareLink>
        <Auctions>
          {auctionsToShow.map((auction, index) => {
            return (
              <AuctionBidItem
                key={auction.id}
                auction={auction}
                last={index === auctionsToShow.length - 1}
                ignoreBid={() => setIgnoredBids([...ignoredBids, auction.id])}
              />
            );
          })}
        </Auctions>
        <InfiniteScrollLoader />
      </MyBidsBannerWrapper>
    </ConditionalWrapper>
  );
};

MyBidsBanner.fragments = {
  auctions: gql`
    fragment MyBidsBanner_auctionConnection on TokenAuctionConnection {
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
      nodes {
        id
        endDate
        myLastBid {
          id
        }
        bestBid {
          id
          ...UseBestBidBelongsToUser_bestBid
        }
        anyCards {
          slug
          ...FlexCard_anyCard
        }
        ...useAuctionCurrentPrice_auction
      }
    }
    ${useBestBidBelongsToUser.fragments.bestBid}
    ${FlexCard.fragments.anyCard}
    ${useAuctionCurrentPrice.fragments.auction}
  ` as TypedDocumentNode<MyBidsBanner_auctionConnection>,
};
